import { useState, useCallback, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AskRequest,
  AskRequestPriorities,
  HoneAbilityActions,
  HoneAbilitySubjects,
  QueueView,
} from '@hone-automation/common';
import { usePatchQueueRequestMutation, useCreateGroup, useBulkAddToGroup, useBulkRemoveFromGroup } from 'hooks/queue';
import { useParams, useSearchParams } from 'react-router-dom';
import { format, isAfter, isBefore, isToday, isThisWeek, isThisMonth, startOfDay } from 'date-fns';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { useQueueContext } from '../../../Queue/QueueContext';
import { useAuthContext } from 'context/useAuthContext';
import { useGetAllUsersByLocation } from 'hooks';
import { MobileFilterChips } from '../MobileFilterChips';
import { useDefaultFilter } from '../../Panels/FilterPanel/hooks';
import { getDropOperation, executeDropOperationDirect } from '../../Panels/FilterPanel/utils';
import { WorkState } from '../../Panels/BrowserPanel/components/RequestCard';
import { ResolveDialog } from '../../Panels/ConversationPanel/components/Header/components/ActionsDropdown/ResolveDialog';
import { ReopenDialog } from '../../Panels/ConversationPanel/components/Header/components/ActionsDropdown/ReopenDialog';
import {
  type DueDatePreset,
  type DateRange,
  type RequestTypeFilter,
  type AssigneeFilterOption,
  requestTypeMapping,
} from '../../Panels/BrowserPanel/components/BrowserToolbar/components/FilterModal';
import { SwipeableRequestCard, SwipeAction, DragPosition } from './SwipeableRequestCard';
import { MobileCollapsibleGroupCard } from './MobileCollapsibleGroupCard';
import { EmptyState } from '../../Common/EmptyState';
import { MultiSelectBar } from '../../Panels/BrowserPanel/components/BrowserToolbar/components/MultiSelectBar';
import { MobileAdvancedFiltersPanel } from '../MobileAdvancedFiltersPanel';
import { MobileSortOptionsPanel } from '../MobileSortOptionsPanel';
import { MobileGroupPickerPanel } from '../MobileGroupPickerPanel';
import { useGroups } from '../../../Dashboard/useGroups';
import { IconTaskNew, IconChevron, IconChevronDown } from 'components/Icons';
import {
  groupRequestsByDate,
  getLastActivityDate,
  DateGroupKey,
  parseLocalDate,
  DateField,
} from '../../Panels/BrowserPanel/utils/dateGrouping';
import { filterRequestsBySearch } from '../../../Queue/utils/filterAndSearchUtils';
import { deriveUserType } from '../../Common';
import { getShortId } from '../../utils/shortId';
import { MobileSkeletonLoading } from './MobileSkeletonLoading';
import type { AssignedToType } from '../../types/Todos';
import type { User } from '@hone-automation/common';
import './MobileRequestList.scss';

/**
 * MobileRequestList - Request list for mobile To-Dos tab
 *
 * Features:
 * - Search and filter chips (matching FilterPanel To-Dos filters)
 * - Date-grouped request list (collapsible sections)
 * - Tap to select request
 *
 * @see RN equivalent: ks-mobile-app/src/screens/QueueList/QueueListScreen.tsx
 */

interface MobileRequestListProps {
  /** Callback when a request is selected */
  onRequestSelect?: (request: AskRequest) => void;
}

/** Advanced filter panel props (for external rendering) */
export interface AdvancedFiltersPanelProps {
  isOpen: boolean;
  onClose: () => void;
  workStateFilter: Set<WorkState>;
  onWorkStateFilterChange: (states: Set<WorkState>) => void;
  workStateCounts: Record<WorkState, number>;
  dueDateFilter: DueDatePreset;
  onDueDateFilterChange: (preset: DueDatePreset) => void;
  dateRangeFilter: DateRange;
  onDateRangeFilterChange: (range: DateRange) => void;
  requestTypeFilter: Set<RequestTypeFilter>;
  onRequestTypeFilterChange: (types: Set<RequestTypeFilter>) => void;
  requestTypeCounts: Record<RequestTypeFilter, number>;
  assigneeFilter: string | null;
  onAssigneeFilterChange: (userId: string | null) => void;
  assigneeOptions: AssigneeFilterOption[];
  onClearAll: () => void;
}

/** Sort options panel props (for external rendering) */
export interface SortOptionsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  dateField: DateField;
  onDateFieldChange: (field: DateField) => void;
}

/** Ref handle for MobileRequestList */
export interface MobileRequestListRef {
  /** Scroll to the currently selected request */
  scrollToSelected: () => void;
  /** Scroll to the top of the list */
  scrollToTop: () => void;
  /** Get props for rendering advanced filters panel externally */
  getAdvancedFiltersPanelProps: () => AdvancedFiltersPanelProps;
  /** Get props for rendering sort options panel externally */
  getSortOptionsPanelProps: () => SortOptionsPanelProps;
}

export const MobileRequestList = forwardRef<MobileRequestListRef, MobileRequestListProps>(
  ({ onRequestSelect }, ref) => {
    const prefix = prefixClass('ksq2-mobile-request-list');
    const { state, dispatch, queryData } = useQueueContext();
    const { selectedItem, activeView, meId } = state;
    const { queryRequests, viewCounts, isQueryLoading } = queryData;
    const { locationId } = useParams<{ locationId: string }>();
    const [searchParams] = useSearchParams();
    const requestIdFromUrl = searchParams.get('requestId');
    const { data: locationUsers } = useGetAllUsersByLocation(locationId);
    const { currentLocationAbility } = useAuthContext();

    // Check if user is accountant (for label swap)
    const isAccountant = currentLocationAbility?.can(HoneAbilityActions.manage, HoneAbilitySubjects.Queue) ?? false;

    // Mutation for swipe actions (resolve, unblock, reopen)
    // Query invalidation is handled by usePatchQueueRequestMutation
    const patchMutation = usePatchQueueRequestMutation(data => {
      if (data && selectedItem?.id === data.id) {
        // Update selected item if it's the same request
        if (selectedItem?.id === data.id) {
          dispatch({ type: 'SET_SELECTED_ITEM', payload: data });
        }
      }
    });

    // Search state (local)
    const [searchQuery, setSearchQuery] = useState('');

    // Quick filter state (group, attachment, mention, assigned)
    const [groupFilterActive, setGroupFilterActive] = useState(false);
    const [attachmentFilterActive, setAttachmentFilterActive] = useState(false);
    const [mentionFilterActive, setMentionFilterActive] = useState(false);
    const [assignedFilterActive, setAssignedFilterActive] = useState(false);

    // Advanced filter state
    const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
    const [workStateFilter, setWorkStateFilter] = useState<Set<WorkState>>(new Set());
    const [dueDateFilter, setDueDateFilter] = useState<DueDatePreset>(null);
    const [dateRangeFilter, setDateRangeFilter] = useState<DateRange>({ from: null, to: null });
    const [requestTypeFilter, setRequestTypeFilter] = useState<Set<RequestTypeFilter>>(new Set());
    const [assigneeAdvancedFilter, setAssigneeAdvancedFilter] = useState<string | null>(null);

    // Sort state (newest first by default, matching desktop behavior)
    const [sortAscending, setSortAscending] = useState(false);
    const [dateField, setDateField] = useState<DateField>('lastMessage');
    const [isSortOptionsOpen, setIsSortOptionsOpen] = useState(false);

    // Collapsed groups (including 'blockers' special group)
    const [collapsedGroups, setCollapsedGroups] = useState<Set<DateGroupKey | 'blockers'>>(new Set());

    // Selection state - always active (no separate "selection mode")
    // Initialize from context's selectedItem if available (e.g., when returning from details or deep link)
    const [selectedIds, setSelectedIds] = useState<(string | number)[]>(() => (selectedItem ? [selectedItem.id] : []));

    // Track if auto-selection has been done for current view (reset on view change)
    const hasAutoSelectedRef = useRef(false);
    const prevActiveViewRef = useRef(activeView);

    // For backwards compatibility with MultiSelectBar - selection mode is when 2+ items selected
    const isSelectionMode = selectedIds.length >= 2;

    // Drag-to-filter state
    const [isDragDropActive, setIsDragDropActive] = useState(false);
    const [dragPosition, setDragPosition] = useState<DragPosition | null>(null);
    const [dragOverFilter, setDragOverFilter] = useState<QueueView | null>(null);
    const filterChipsRef = useRef<HTMLDivElement>(null);

    // Resolve dialog state (single selection only)
    const [isResolveOpen, setIsResolveOpen] = useState(false);
    const [resolveTarget, setResolveTarget] = useState<AskRequest | null>(null);
    const [isReopenOpen, setIsReopenOpen] = useState(false);
    const [reopenTarget, setReopenTarget] = useState<AskRequest | null>(null);
    const [isBulkResolveOpen, setIsBulkResolveOpen] = useState(false);
    const [isBulkReopenOpen, setIsBulkReopenOpen] = useState(false);
    const [bulkReopenInfo, setBulkReopenInfo] = useState<string | null>(null);

    // Groups state
    const groups = useGroups();
    const [isGroupPickerOpen, setIsGroupPickerOpen] = useState(false);
    const [groupPickerMode, setGroupPickerMode] = useState<'browse' | 'assign'>('browse');
    const [pendingDropIds, setPendingDropIds] = useState<(string | number)[]>([]);

    // Pull-to-refresh state
    const queryClient = useQueryClient();
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    const pullStartY = useRef<number | null>(null);
    const pullDistanceRef = useRef(0); // Ref to access current value in event listener
    const PULL_THRESHOLD = 80; // Distance to trigger refresh

    // Keep pullDistanceRef in sync
    useEffect(() => {
      pullDistanceRef.current = pullDistance;
    }, [pullDistance]);

    // Auto-select default filter on load (blockers > me > myTeam > client)
    useDefaultFilter({
      filterCounts: viewCounts,
      activeView,
      onSetView: (view: QueueView | undefined) => dispatch({ type: 'SET_ACTIVE_VIEW', payload: view }),
      dataLoaded: queryRequests.length > 0 || !isQueryLoading,
    });

    // Request card users for mentions
    const requestCardUsers = useMemo<User[] | undefined>(() => {
      if (!locationUsers) return undefined;
      return (locationUsers as any[]).map(u => {
        const name = u.name || u.username || u.email?.split('@')?.[0] || u.userId;
        const username = u.username || u.email || u.userId;
        return { userId: u.userId, name, username, deleted: u.deleted } as User;
      });
    }, [locationUsers]);

    // Server-filtered requests from QueueContext (filtered by activeView on backend)
    const viewFilteredRequests = queryRequests;

    // Quick filter helper functions
    const checkHasMention = useCallback(
      (request: AskRequest): boolean => {
        if (!meId || !request.comments) return false;
        return request.comments.some((comment: any) => {
          const commentText = comment?.comment || '';
          return commentText.includes(`::${meId}::`);
        });
      },
      [meId]
    );

    const checkHasAttachment = useCallback((request: AskRequest): boolean => {
      return request.comments?.some((comment: any) => comment.attachments?.length > 0) || false;
    }, []);

    const checkIsAssigned = useCallback((request: AskRequest): boolean => {
      return !!request.assignee;
    }, []);

    // Quick filter counts (based on view-filtered requests)
    const quickFilterCounts = useMemo(() => {
      let mentionCount = 0;
      let attachmentCount = 0;
      let assignedCount = 0;

      viewFilteredRequests.forEach(req => {
        if (checkHasMention(req)) mentionCount++;
        if (checkHasAttachment(req)) attachmentCount++;
        if (checkIsAssigned(req)) assignedCount++;
      });

      return { mentionCount, attachmentCount, assignedCount };
    }, [viewFilteredRequests, checkHasMention, checkHasAttachment, checkIsAssigned]);

    // Work state counts for advanced filters
    const workStateCounts = useMemo(() => {
      const counts: Record<WorkState, number> = {
        backlog: 0,
        todo: 0,
        'in-progress': 0,
        blocked: 0,
        closed: 0,
      };

      viewFilteredRequests.forEach(req => {
        // @ts-ignore
        if (req.priority === 'Blocker') {
          counts.blocked++;
        } else if (req.status === 'Closed') {
          counts.closed++;
        } else if (req.status === 'In Progress') {
          counts['in-progress']++;
        } else if (req.status === 'Open') {
          counts.todo++; // All open items count as 'todo', regardless of assignee
        }
      });

      return counts;
    }, [viewFilteredRequests]);

    // Request type counts for advanced filters
    const requestTypeCounts = useMemo(() => {
      const counts: Record<RequestTypeFilter, number> = {
        email: 0,
        transaction: 0,
        'flagged-cell': 0,
        'custom-request': 0,
      };

      viewFilteredRequests.forEach(req => {
        const reqType = req.type;
        // Map request type to filter category
        (Object.keys(requestTypeMapping) as RequestTypeFilter[]).forEach(filterType => {
          if (requestTypeMapping[filterType].includes(reqType as any)) {
            counts[filterType]++;
          }
        });
      });

      return counts;
    }, [viewFilteredRequests]);

    // Assignee options for advanced filters
    const assigneeFilterOptions = useMemo<AssigneeFilterOption[]>(() => {
      if (!locationUsers) return [];
      return (locationUsers as any[]).map(u => ({
        userId: u.userId,
        name: u.name || u.username || u.email?.split('@')?.[0] || u.userId,
      }));
    }, [locationUsers]);

    // Quick filter toggle handlers
    const handleGroupFilterToggle = useCallback(() => {
      setGroupFilterActive(prev => !prev);
    }, []);

    const handleMentionFilterToggle = useCallback(() => {
      setMentionFilterActive(prev => !prev);
    }, []);

    const handleAttachmentFilterToggle = useCallback(() => {
      setAttachmentFilterActive(prev => !prev);
    }, []);

    const handleAssignedFilterToggle = useCallback(() => {
      setAssignedFilterActive(prev => !prev);
    }, []);

    // Advanced filter handlers
    const handleAdvancedFilterClick = useCallback(() => {
      setIsAdvancedFiltersOpen(true);
    }, []);

    const handleAdvancedFiltersClose = useCallback(() => {
      setIsAdvancedFiltersOpen(false);
    }, []);

    const handleClearAllAdvancedFilters = useCallback(() => {
      setWorkStateFilter(new Set());
      setDueDateFilter(null);
      setDateRangeFilter({ from: null, to: null });
      setRequestTypeFilter(new Set());
      setAssigneeAdvancedFilter(null);
    }, []);

    // Sort handlers
    const handleSortOptionsClick = useCallback(() => {
      setIsSortOptionsOpen(true);
    }, []);

    const handleSortOptionsClose = useCallback(() => {
      setIsSortOptionsOpen(false);
    }, []);

    const handleSortToggle = useCallback(() => {
      setSortAscending(prev => !prev);
    }, []);

    const handleDateFieldChange = useCallback((field: DateField) => {
      setDateField(field);
      setIsSortOptionsOpen(false);
    }, []);

    // Groups pill click handler - opens picker in browse mode
    const handleGroupsChipClick = useCallback(() => {
      setGroupPickerMode('browse');
      setIsGroupPickerOpen(true);
    }, []);

    // Handle group selection in browse mode (navigate to group view)
    const handleGroupSelect = useCallback(
      (groupId: string) => {
        const group = groups.find(g => g.id === groupId);
        if (!group) return;

        dispatch({ type: 'SET_ACTIVE_GROUP_ID', payload: groupId });
        dispatch({ type: 'SET_ACTIVE_VIEW', payload: 'Groups' as QueueView });

        // Create a synthetic AskRequest for the group container so the list can show children
        const syntheticGroupRequest = {
          id: groupId,
          identifier: group.name,
          type: 'Group',
          status: 'Open',
          comments: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as unknown as AskRequest;
        dispatch({ type: 'SET_SELECTED_ITEM', payload: syntheticGroupRequest });

        setSelectedIds([]);
        hasAutoSelectedRef.current = false;
      },
      [groups, dispatch]
    );

    // Group hooks (for assign/remove/create operations)
    const { createGroup } = useCreateGroup(locationId || '');
    const { addMultipleToGroup } = useBulkAddToGroup(locationId || '');
    const { removeMultipleFromGroup } = useBulkRemoveFromGroup(locationId || '');

    // Handle assign to group (from drag-to-Groups or action panel)
    const handleAssignToGroup = useCallback(
      (groupId: string) => {
        const ids = pendingDropIds.length > 0 ? pendingDropIds : selectedIds;
        if (ids.length === 0) return;
        addMultipleToGroup(ids.map(String), groupId);

        setPendingDropIds([]);
        setIsGroupPickerOpen(false);

        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      },
      [pendingDropIds, selectedIds, addMultipleToGroup]
    );

    // Unified group select handler based on mode
    const handleGroupPickerSelect = useCallback(
      (groupId: string) => {
        if (groupPickerMode === 'browse') {
          handleGroupSelect(groupId);
        } else {
          handleAssignToGroup(groupId);
        }
      },
      [groupPickerMode, handleGroupSelect, handleAssignToGroup]
    );

    // Check if any advanced filter is active
    const hasActiveAdvancedFilter = useMemo(
      () =>
        workStateFilter.size > 0 ||
        dueDateFilter !== null ||
        dateRangeFilter.from !== null ||
        dateRangeFilter.to !== null ||
        requestTypeFilter.size > 0 ||
        assigneeAdvancedFilter !== null,
      [workStateFilter, dueDateFilter, dateRangeFilter, requestTypeFilter, assigneeAdvancedFilter]
    );

    // Get work state for a request (defined early so it can be used by filters)
    // Maps open/unassigned to 'todo' (not 'backlog') to match desktop behavior
    // and ensure items aren't hidden when status filters are applied
    const getWorkState = (request: AskRequest): WorkState | undefined => {
      // @ts-ignore
      if (request.priority === 'Blocker') return 'blocked';
      if (request.status === 'Closed') return 'closed';
      if (request.status === 'In Progress') return 'in-progress';
      if (request.status === 'Open') {
        return 'todo'; // All open items are 'todo', regardless of assignee
      }
      return undefined;
    };

    // Search filter function - uses the shared utility that supports:
    // - Text search: identifier, locationId, comment, transaction fields (id, externalId, name, account, memo)
    // - Amount search with operators: >100 or <50 (searches flaggedAmount and transaction.amount)
    // - All comments (not just the last one)
    const applySearchFilter = useCallback(
      (reqs: AskRequest[]) => {
        if (!searchQuery.trim()) return reqs;
        return filterRequestsBySearch(reqs, searchQuery);
      },
      [searchQuery]
    );

    // Apply quick filters
    const applyQuickFilters = useCallback(
      (reqs: AskRequest[]) => {
        return reqs.filter(req => {
          // If no quick filters active, show all
          if (!mentionFilterActive && !attachmentFilterActive && !assignedFilterActive) {
            return true;
          }
          // If any quick filter is active, item must match at least one active filter
          const matchesMention = mentionFilterActive && checkHasMention(req);
          const matchesAttachment = attachmentFilterActive && checkHasAttachment(req);
          const matchesAssigned = assignedFilterActive && checkIsAssigned(req);

          return matchesMention || matchesAttachment || matchesAssigned;
        });
      },
      [
        mentionFilterActive,
        attachmentFilterActive,
        assignedFilterActive,
        checkHasMention,
        checkHasAttachment,
        checkIsAssigned,
      ]
    );

    // Apply advanced filters
    const applyAdvancedFilters = useCallback(
      (reqs: AskRequest[]) => {
        return reqs.filter(req => {
          // Work state filter
          if (workStateFilter.size > 0) {
            const reqWorkState = getWorkState(req);
            if (!reqWorkState || !workStateFilter.has(reqWorkState)) {
              return false;
            }
          }

          // Due date filter
          // Use parseLocalDate to avoid UTC timezone issues with YYYY-MM-DD strings
          if (dueDateFilter) {
            const dueDate = req.dueDate ? parseLocalDate(req.dueDate) : null;

            switch (dueDateFilter) {
              case 'overdue':
                if (!dueDate || !isBefore(startOfDay(dueDate), startOfDay(new Date()))) {
                  return false;
                }
                break;
              case 'today':
                if (!dueDate || !isToday(dueDate)) {
                  return false;
                }
                break;
              case 'this-week':
                if (!dueDate || !isThisWeek(dueDate)) {
                  return false;
                }
                break;
              case 'this-month':
                if (!dueDate || !isThisMonth(dueDate)) {
                  return false;
                }
                break;
              case 'no-due-date':
                if (dueDate) {
                  return false;
                }
                break;
            }
          }

          // Date range filter (on lastMessage date)
          if (dateRangeFilter.from || dateRangeFilter.to) {
            const lastActivityDate = getLastActivityDate(req);
            if (dateRangeFilter.from && isBefore(lastActivityDate, startOfDay(dateRangeFilter.from))) {
              return false;
            }
            if (dateRangeFilter.to && isAfter(lastActivityDate, dateRangeFilter.to)) {
              return false;
            }
          }

          // Request type filter
          if (requestTypeFilter.size > 0) {
            const reqType = req.type;
            const matchesAnyType = Array.from(requestTypeFilter).some(filterType =>
              requestTypeMapping[filterType].includes(reqType as any)
            );
            if (!matchesAnyType) {
              return false;
            }
          }

          // Assignee filter
          if (assigneeAdvancedFilter) {
            if (req.assignee !== assigneeAdvancedFilter) {
              return false;
            }
          }

          return true;
        });
      },
      [workStateFilter, dueDateFilter, dateRangeFilter, requestTypeFilter, assigneeAdvancedFilter]
    );

    // Apply group filter (show only collapsible group cards, children render inside them)
    const applyGroupFilter = useCallback(
      (reqs: AskRequest[]) => {
        if (!groupFilterActive) return reqs;
        return reqs.filter(req => req.type === 'Group');
      },
      [groupFilterActive]
    );

    // Filter function for group children (search + quick + advanced, no group filter)
    const filterChildRequests = useCallback(
      (reqs: AskRequest[]) => applyAdvancedFilters(applyQuickFilters(applySearchFilter(reqs))),
      [applySearchFilter, applyQuickFilters, applyAdvancedFilters]
    );

    // Apply search, quick filters, advanced filters, and group filter to server-filtered requests
    const allFilteredRequests = useMemo(
      () => applyGroupFilter(applyAdvancedFilters(applyQuickFilters(applySearchFilter(viewFilteredRequests)))),
      [viewFilteredRequests, applySearchFilter, applyQuickFilters, applyAdvancedFilters, applyGroupFilter]
    );

    // Filter out child requests from top-level display — they render inside MobileCollapsibleGroupCard.
    // Groups are only included if they have at least one visible child (matching desktop behavior).
    // When viewing a specific group's contents, show all (they're the children).
    const topLevelRequests = useMemo(() => {
      if (activeView === 'Groups' && state.activeGroupId) {
        return allFilteredRequests;
      }
      // In Blockers view, grouped items must still surface — only exclude Group containers
      if (activeView === 'Blockers') {
        return allFilteredRequests.filter(r => r.type !== 'Group');
      }
      return allFilteredRequests.filter(r => {
        if (r.parentId) return false; // Children render inside their group card
        if (r.type === 'Group') {
          // Only include groups that have at least one visible child
          const children = queryRequests.filter(c => c.parentId === r.id && c.type !== 'Group');
          return filterChildRequests(children).length > 0;
        }
        return true;
      });
    }, [allFilteredRequests, activeView, state.activeGroupId, queryRequests, filterChildRequests]);

    // Separate blockers from non-blockers for display
    // For Me/Team/Client views, blockers are shown at the top in a separate section
    const filteredViewBlockers = useMemo(() => {
      // Show separate blockers section for Me/Team/Client/Groups views
      if (activeView === 'Blockers' || activeView === 'Completed' || !activeView) {
        return [];
      }
      // Include child blockers so grouped items still appear in the blockers section
      return allFilteredRequests.filter(req => req.priority === 'Blocker');
    }, [allFilteredRequests, activeView]);

    // Non-blocker requests (or all requests for Blockers/Completed views)
    const filteredRequests = useMemo(() => {
      if (activeView === 'Blockers' || activeView === 'Completed' || !activeView) {
        return topLevelRequests;
      }
      return topLevelRequests.filter(req => req.priority !== 'Blocker');
    }, [topLevelRequests, activeView]);

    // Group requests by date (use state values for sort direction and date field)
    const dateGroups = useMemo(() => {
      return groupRequestsByDate(filteredRequests, sortAscending, dateField);
    }, [filteredRequests, sortAscending, dateField]);

    const updateRequestIdInUrl = useCallback((requestId: string | undefined) => {
      const url = new URL(window.location.href);
      if (requestId) {
        url.searchParams.set('requestId', requestId);
      } else {
        url.searchParams.delete('requestId');
      }
      window.history.replaceState({}, '', url.toString());
    }, []);

    // Reset auto-selection flag when view changes
    useEffect(() => {
      if (prevActiveViewRef.current !== activeView) {
        hasAutoSelectedRef.current = false;
        prevActiveViewRef.current = activeView;
      }
    }, [activeView]);

    // Track previous selectedItem to detect external changes (deep links, context updates)
    const prevSelectedItemIdRef = useRef<string | undefined>(selectedItem?.id);

    // Sync local selection state with context's selectedItem
    // Only sync when selectedItem changes externally (e.g., deep link, context update)
    // Do NOT include selectedIds in deps - we don't want to override user's local selections
    useEffect(() => {
      const prevId = prevSelectedItemIdRef.current;
      const currentId = selectedItem?.id;

      // Only sync if selectedItem actually changed (external change) and is not a group
      if (currentId && currentId !== prevId && selectedItem?.type !== 'Group') {
        setSelectedIds([currentId]);
        hasAutoSelectedRef.current = true;
      }

      prevSelectedItemIdRef.current = currentId;
    }, [selectedItem]);

    // Auto-select first item when:
    // 1. Data is loaded (not loading)
    // 2. No requestId in URL (deep link handled by QueueContext)
    // 3. Haven't auto-selected yet for this view
    // 4. No selectedItem from context
    useEffect(() => {
      if (isQueryLoading) return;
      if (hasAutoSelectedRef.current) return;
      if (requestIdFromUrl) return; // QueueContext handles deep links

      const allVisibleItems = [...filteredViewBlockers, ...filteredRequests];
      const selectableItems = allVisibleItems.filter(r => r.type !== 'Group');
      if (selectableItems.length === 0) return;

      // Context already has selection - just sync local state (skip groups)
      if (selectedItem && selectedItem.type !== 'Group' && selectableItems.some(r => r.id === selectedItem.id)) {
        setSelectedIds([selectedItem.id]);
        hasAutoSelectedRef.current = true;
        return;
      }

      // Auto-select first non-group item
      const firstItem = selectableItems[0];
      setSelectedIds([firstItem.id]);
      dispatch({ type: 'SET_SELECTED_ITEM', payload: firstItem });
      updateRequestIdInUrl(firstItem.id);
      hasAutoSelectedRef.current = true;
    }, [
      isQueryLoading,
      filteredRequests,
      filteredViewBlockers,
      requestIdFromUrl,
      selectedItem,
      dispatch,
      updateRequestIdInUrl,
    ]);

    // Scroll to selected item when selection changes (filter change, mount, etc.)
    const prevSelectedIdRef = useRef<string | number | null>(null);
    useEffect(() => {
      const currentSelectedId = selectedIds[0];
      // Only scroll if selection changed and we have a selection
      if (currentSelectedId && currentSelectedId !== prevSelectedIdRef.current && listRef.current) {
        const timer = setTimeout(() => {
          const list = listRef.current;
          if (!list) return;

          const selectedCard = list.querySelector(`[data-request-id="${currentSelectedId}"]`) as HTMLElement | null;
          if (selectedCard) {
            // Use getBoundingClientRect for reliable positioning regardless of DOM hierarchy
            const listRect = list.getBoundingClientRect();
            const cardRect = selectedCard.getBoundingClientRect();

            // Calculate card's position relative to the list's scroll container
            const cardTopRelativeToList = cardRect.top - listRect.top + list.scrollTop;
            const cardHeight = cardRect.height;
            const listHeight = listRect.height;

            // Center the card in the viewport
            const scrollTo = cardTopRelativeToList - listHeight / 2 + cardHeight / 2;

            list.scrollTo({ top: Math.max(0, scrollTo), behavior: 'auto' });
          }
        }, 150);
        prevSelectedIdRef.current = currentSelectedId;
        return () => clearTimeout(timer);
      }
      prevSelectedIdRef.current = currentSelectedId ?? null;
    }, [selectedIds]);

    // Handle filter change - dispatch to QueueContext and reset selection
    const handleFilterChange = useCallback(
      (filter: QueueView | undefined) => {
        dispatch({ type: 'SET_ACTIVE_VIEW', payload: filter });
        // Clear selection in both context and local state
        dispatch({ type: 'SET_SELECTED_ITEM', payload: null });
        setSelectedIds([]);
        hasAutoSelectedRef.current = false;
        // Clear requestId from URL when changing filters
        updateRequestIdInUrl(undefined);
        // Auto-deactivate group filter when switching to Groups view
        if (filter === 'Groups') setGroupFilterActive(false);
      },
      [dispatch, updateRequestIdInUrl]
    );

    // Toggle group collapse
    const toggleGroup = useCallback((groupKey: DateGroupKey | 'blockers') => {
      setCollapsedGroups(prev => {
        const newSet = new Set(prev);
        if (newSet.has(groupKey)) {
          newSet.delete(groupKey);
        } else {
          newSet.add(groupKey);
        }
        return newSet;
      });
    }, []);

    // Toggle all groups (expand all if any collapsed, collapse all if all expanded)
    const toggleAllGroups = useCallback(() => {
      const allGroupKeys: (DateGroupKey | 'blockers')[] = [
        ...dateGroups.map(g => g.key),
        ...(filteredViewBlockers.length > 0 ? (['blockers'] as const) : []),
      ];
      const allCollapsed = allGroupKeys.every(key => collapsedGroups.has(key));
      if (allCollapsed) {
        setCollapsedGroups(new Set());
      } else {
        setCollapsedGroups(new Set(allGroupKeys));
      }
    }, [dateGroups, filteredViewBlockers.length, collapsedGroups]);

    // Double-tap detection for group headers
    const lastTapTimeRef = useRef<number>(0);
    const DOUBLE_TAP_THRESHOLD = 300; // ms

    const handleGroupHeaderTap = useCallback(
      (groupKey: DateGroupKey | 'blockers') => {
        const now = Date.now();
        if (now - lastTapTimeRef.current < DOUBLE_TAP_THRESHOLD) {
          // Double tap - toggle all groups
          toggleAllGroups();
          lastTapTimeRef.current = 0; // Reset to prevent triple-tap
        } else {
          // Single tap - toggle this group
          toggleGroup(groupKey);
          lastTapTimeRef.current = now;
        }
      },
      [toggleGroup, toggleAllGroups]
    );

    // Handle request card tap
    // - In selection mode (2+ items): toggle selection (same as swipe-right)
    // - Normal mode: open details and focus on this item
    const handleRequestTap = useCallback(
      (request: AskRequest) => {
        // Groups don't participate in selection — just open details
        if (request.type === 'Group') {
          updateRequestIdInUrl(request.id);
          dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
          onRequestSelect?.(request);
          return;
        }

        // In selection mode, tap toggles selection instead of opening details
        if (selectedIds.length >= 2) {
          const isCurrentlySelected = selectedIds.includes(request.id);
          if (isCurrentlySelected) {
            // Deselect (but keep at least one)
            if (selectedIds.length > 1) {
              const newSelection = selectedIds.filter(id => id !== request.id);
              setSelectedIds(newSelection);
              // If this was the focused item, move focus to another selected item
              if (selectedItem?.id === request.id && newSelection.length > 0) {
                const nextFocusId = newSelection[0];
                const nextFocusItem = queryRequests.find(r => r.id === nextFocusId);
                if (nextFocusItem) {
                  dispatch({ type: 'SET_SELECTED_ITEM', payload: nextFocusItem });
                }
              }
            }
          } else {
            // Add to selection
            setSelectedIds(prev => [...prev, request.id]);
          }
          return;
        }

        // Normal mode: reset selection to just this item and open details
        setSelectedIds([request.id]);
        // Update URL with requestId for deep linking
        updateRequestIdInUrl(request.id);
        // Open details
        dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
        onRequestSelect?.(request);
      },
      [dispatch, onRequestSelect, selectedIds, selectedItem, queryRequests, updateRequestIdInUrl]
    );

    // Handle swipe right - toggle selection (can't deselect the last one)
    const handleSwipeRightSelect = useCallback(
      (request: AskRequest) => {
        // Groups don't participate in multi-select
        if (request.type === 'Group') return;

        const isCurrentlySelected = selectedIds.includes(request.id);

        if (isCurrentlySelected) {
          // Can't deselect if it's the only one
          if (selectedIds.length <= 1) {
            return;
          }

          // Deselect
          const newSelection = selectedIds.filter(id => id !== request.id);
          setSelectedIds(newSelection);

          // If this was the focused item, move focus to another selected item
          if (selectedItem?.id === request.id && newSelection.length > 0) {
            const nextFocusId = newSelection[0];
            const nextFocusItem = queryRequests.find(r => r.id === nextFocusId);
            if (nextFocusItem) {
              dispatch({ type: 'SET_SELECTED_ITEM', payload: nextFocusItem });
            }
          }
        } else {
          // Add to selection
          setSelectedIds(prev => [...prev, request.id]);
        }
      },
      [selectedIds, selectedItem, queryRequests, dispatch]
    );

    // Handle long press - DISABLED (keeping code for reference)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleLongPress = useCallback((_request: AskRequest) => {
      // Disabled - swipe right is now used for selection
      // Original behavior:
      // setSelectedIds([request.id]);
    }, []);

    // Exit selection mode - resets to single selection (keeps first selected)
    const exitSelectionMode = useCallback(() => {
      setSelectedIds(prev => (prev.length > 0 ? [prev[0]] : []));
    }, []);

    // Multi-select group handlers (must be after exitSelectionMode)
    const handleCreateGroup = useCallback(async () => {
      if (selectedIds.length === 0) return;
      const title = prompt('Enter group name:');
      if (!title?.trim()) return;

      await createGroup({ title: title.trim(), childRequestIds: selectedIds.map(String) });
      exitSelectionMode();
    }, [selectedIds, createGroup, exitSelectionMode]);

    const handleMultiSelectAddToGroup = useCallback(
      async (groupId: string) => {
        await addMultipleToGroup(selectedIds.map(String), groupId);
        exitSelectionMode();
      },
      [selectedIds, addMultipleToGroup, exitSelectionMode]
    );

    const handleRemoveFromGroup = useCallback(async () => {
      await removeMultipleFromGroup(selectedIds.map(String), state.activeGroupId ?? undefined);
      exitSelectionMode();
    }, [selectedIds, removeMultipleFromGroup, state.activeGroupId, exitSelectionMode]);

    // Handle swipe actions (resolve, unblock, reopen, etc.)
    const handleSwipeAction = useCallback(
      (action: SwipeAction, request: AskRequest) => {
        switch (action) {
          case 'resolve':
            // Single selection: show resolve dialog for optional comment
            if (selectedIds.length <= 1) {
              setResolveTarget(request);
              setIsResolveOpen(true);
              break;
            }
            // Multi-selection: resolve immediately
            patchMutation.mutate({ id: request.id, status: 'Closed', locationId });
            break;
          case 'unblock':
            // Remove blocker priority
            patchMutation.mutate({ id: request.id, priority: AskRequestPriorities.Normal, locationId });
            break;
          case 'block':
            // Set blocker priority
            patchMutation.mutate({ id: request.id, priority: AskRequestPriorities.Blocker, locationId });
            break;
          case 'reopen':
            // Reopen closed request
            if (!request.canReopen) {
              break;
            }
            if (selectedIds.length <= 1) {
              setReopenTarget(request);
              setIsReopenOpen(true);
              break;
            }
            patchMutation.mutate({ id: request.id, status: 'Open', locationId });
            break;
          case 'assign':
            // Assignment is handled by AssignmentButton directly
            break;
        }
      },
      [patchMutation, locationId, selectedIds.length]
    );

    const handleResolveSubmit = useCallback(
      ({ comment }: { comment: string }) => {
        if (!resolveTarget) return;
        patchMutation.mutate({ id: resolveTarget.id, status: 'Closed', comment });
        setIsResolveOpen(false);
        setResolveTarget(null);
      },
      [patchMutation, resolveTarget]
    );

    const handleReopenSubmit = useCallback(
      ({ comment }: { comment: string }) => {
        if (!reopenTarget) return;
        patchMutation.mutate({ id: reopenTarget.id, status: 'Open', comment });
        setIsReopenOpen(false);
        setReopenTarget(null);
      },
      [patchMutation, reopenTarget]
    );

    // Drag-to-filter handlers
    const handleDragStart = useCallback(() => {
      setIsDragDropActive(true);
    }, []);

    const handleDragMove = useCallback((position: DragPosition) => {
      setDragPosition(position);

      // Hit test to see if finger is over a filter chip
      if (filterChipsRef.current) {
        const chips = filterChipsRef.current.querySelectorAll('[data-filter-id]');
        let foundFilter: QueueView | null = null;
        for (const chip of chips) {
          const rect = chip.getBoundingClientRect();
          if (
            position.x >= rect.left &&
            position.x <= rect.right &&
            position.y >= rect.top &&
            position.y <= rect.bottom
          ) {
            foundFilter = chip.getAttribute('data-filter-id') as QueueView;
            break;
          }
        }
        setDragOverFilter(foundFilter);
      }
    }, []);

    const handleDragEnd = useCallback(
      (position: DragPosition) => {
        // Hit test against filter chips
        if (filterChipsRef.current) {
          const chips = filterChipsRef.current.querySelectorAll('[data-filter-id]');
          for (const chip of chips) {
            const rect = chip.getBoundingClientRect();
            if (
              position.x >= rect.left &&
              position.x <= rect.right &&
              position.y >= rect.top &&
              position.y <= rect.bottom
            ) {
              const targetFilter = chip.getAttribute('data-filter-id') as QueueView;
              if (targetFilter) {
                // Special handling for Groups drop target
                if (targetFilter === 'Groups') {
                  setPendingDropIds([...selectedIds]);
                  setGroupPickerMode('assign');
                  setIsGroupPickerOpen(true);

                  setIsDragDropActive(false);
                  setDragPosition(null);
                  setDragOverFilter(null);

                  if (navigator.vibrate) {
                    navigator.vibrate(50);
                  }
                  return;
                }

                // Get the drop operation based on source (activeView) and target filter
                const dropOperation = getDropOperation(activeView, targetFilter);

                if (dropOperation.allowed) {
                  // Execute the drop operation using shared utility
                  // This splits mutations by field type (assignee, status, priority separately)
                  executeDropOperationDirect(selectedIds, dropOperation, meId, payload =>
                    patchMutation.mutate({ ...payload, locationId } as { id: string; locationId?: string })
                  );

                  // Trigger highlight animation on the target filter chip
                  dispatch({ type: 'HIGHLIGHT_FILTER', payload: targetFilter });
                  setTimeout(() => {
                    dispatch({ type: 'HIGHLIGHT_FILTER', payload: null });
                  }, 500);

                  // Select the next item in the current list (not the moved items)
                  // Find items that will remain in the current view after the move
                  const allVisibleItems = [...filteredViewBlockers, ...filteredRequests];
                  const remainingItems = allVisibleItems.filter(r => !selectedIds.includes(r.id));

                  if (remainingItems.length > 0) {
                    // Find the index of the first selected item in the current list
                    const firstSelectedIndex = allVisibleItems.findIndex(r => selectedIds.includes(r.id));
                    // Try to select the item at the same position, or the last item if out of bounds
                    const nextIndex = Math.min(firstSelectedIndex, remainingItems.length - 1);
                    const nextItem = remainingItems[Math.max(0, nextIndex)];
                    setSelectedIds([nextItem.id]);
                    dispatch({ type: 'SET_SELECTED_ITEM', payload: nextItem });
                  } else {
                    // No items remaining, clear selection
                    setSelectedIds([]);
                    dispatch({ type: 'SET_SELECTED_ITEM', payload: null });
                  }

                  if (navigator.vibrate) {
                    navigator.vibrate(50);
                  }
                }
              }
              break;
            }
          }
        }
        setIsDragDropActive(false);
        setDragPosition(null);
        setDragOverFilter(null);
      },
      [dispatch, activeView, selectedIds, filteredViewBlockers, filteredRequests, patchMutation, meId, locationId]
    );

    const handleDragCancel = useCallback(() => {
      setIsDragDropActive(false);
      setDragPosition(null);
      setDragOverFilter(null);
    }, []);

    // MultiSelectBar handlers
    const handleSelectAll = useCallback(() => {
      const allIds = filteredRequests.filter(r => r.type !== 'Group').map(r => r.id);
      setSelectedIds(allIds);
    }, [filteredRequests]);

    const handleDeselectAll = useCallback(() => {
      exitSelectionMode();
    }, [exitSelectionMode]);

    const handleBulkAssign = useCallback(
      (userId: string | null) => {
        if (selectedIds.length === 0) return;

        // Patch all selected requests with the new assignee
        selectedIds.forEach(id => {
          const request = queryRequests.find(r => r.id === id);
          if (request) {
            patchMutation.mutate({ id: String(id), assignee: userId, locationId });
          }
        });

        exitSelectionMode();
      },
      [selectedIds, queryRequests, patchMutation, exitSelectionMode, locationId]
    );

    const handleBulkSetDueDate = useCallback(
      (date: Date | null) => {
        if (selectedIds.length === 0) return;

        // Format date for API (or null to clear)
        const dueDateValue = date ? format(date, 'yyyy-MM-dd') : null;

        // Patch all selected requests with the new due date
        selectedIds.forEach(id => {
          const request = queryRequests.find(r => r.id === id);
          if (request) {
            patchMutation.mutate({ id: String(id), dueDate: dueDateValue, locationId });
          }
        });

        exitSelectionMode();
      },
      [selectedIds, queryRequests, patchMutation, exitSelectionMode, locationId]
    );

    const handleBulkResolve = useCallback(() => {
      if (selectedIds.length === 0) return;
      setIsBulkResolveOpen(true);
    }, [selectedIds.length]);

    const handleBulkReopen = useCallback(() => {
      if (selectedIds.length === 0) return;
      const reopenableCount = selectedIds.reduce((count, id) => {
        const request = queryRequests.find(r => r.id === id);
        return request?.canReopen ? count + 1 : count;
      }, 0);

      if (reopenableCount !== selectedIds.length) {
        setBulkReopenInfo(
          `${reopenableCount} of ${selectedIds.length} selected can be reopened. Others will be skipped.`
        );
      } else {
        setBulkReopenInfo(null);
      }
      setIsBulkReopenOpen(true);
    }, [selectedIds, queryRequests]);

    const handleBulkResolveSubmit = useCallback(
      async ({ comment }: { comment: string }) => {
        if (selectedIds.length === 0) {
          setIsBulkResolveOpen(false);
          return;
        }

        // Patch all selected requests to closed status
        for (const id of selectedIds) {
          const request = queryRequests.find(r => r.id === id);
          if (request) {
            patchMutation.mutate({ id: String(id), status: 'Closed', locationId });
          }
        }

        setIsBulkResolveOpen(false);
        exitSelectionMode();
      },
      [selectedIds, queryRequests, patchMutation, exitSelectionMode]
    );

    const handleBulkReopenSubmit = useCallback(
      async ({ comment }: { comment: string }) => {
        if (selectedIds.length === 0) {
          setIsBulkReopenOpen(false);
          setBulkReopenInfo(null);
          return;
        }

        // Patch all reopenable selected requests to open status
        for (const id of selectedIds) {
          const request = queryRequests.find(r => r.id === id);
          if (request?.canReopen) {
            await patchMutation.mutateAsync({ id: String(id), status: 'Open', comment, locationId });
          }
        }

        setIsBulkReopenOpen(false);
        setBulkReopenInfo(null);
        exitSelectionMode();
      },
      [selectedIds, queryRequests, patchMutation, exitSelectionMode]
    );

    const handleBulkToggleBlocker = useCallback(
      (isBlocker: boolean) => {
        if (selectedIds.length === 0) return;

        // Patch all selected requests with the appropriate priority
        const priority = isBlocker ? AskRequestPriorities.Blocker : AskRequestPriorities.Normal;
        selectedIds.forEach(id => {
          const request = queryRequests.find(r => r.id === id);
          if (request) {
            patchMutation.mutate({ id: String(id), priority, locationId });
          }
        });

        exitSelectionMode();
      },
      [selectedIds, queryRequests, patchMutation, exitSelectionMode, locationId]
    );

    // Pull-to-refresh handlers
    const isRefreshingRef = useRef(false);
    useEffect(() => {
      isRefreshingRef.current = isRefreshing;
    }, [isRefreshing]);

    // Scroll to the selected item in the list (without affecting parent scroll)
    const scrollToSelected = useCallback(
      (smooth = true) => {
        if (!listRef.current || selectedIds.length === 0) return;

        const list = listRef.current;
        const selectedId = selectedIds[0];
        const selectedCard = list.querySelector(`[data-request-id="${selectedId}"]`) as HTMLElement | null;

        if (selectedCard) {
          // Use getBoundingClientRect for reliable positioning regardless of DOM hierarchy
          const listRect = list.getBoundingClientRect();
          const cardRect = selectedCard.getBoundingClientRect();

          // Calculate card's position relative to the list's scroll container
          const cardTopRelativeToList = cardRect.top - listRect.top + list.scrollTop;
          const cardHeight = cardRect.height;
          const listHeight = listRect.height;

          // Center the card in the viewport
          const scrollTo = cardTopRelativeToList - listHeight / 2 + cardHeight / 2;

          list.scrollTo({
            top: Math.max(0, scrollTo),
            behavior: smooth ? 'smooth' : 'auto',
          });
        }
      },
      [selectedIds]
    );

    // Scroll to the top of the list
    const scrollToTop = useCallback(() => {
      if (!listRef.current) return;
      listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Expose scroll functions and panel props to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        scrollToSelected: () => scrollToSelected(true),
        scrollToTop,
        getAdvancedFiltersPanelProps: (): AdvancedFiltersPanelProps => ({
          isOpen: isAdvancedFiltersOpen,
          onClose: handleAdvancedFiltersClose,
          workStateFilter,
          onWorkStateFilterChange: setWorkStateFilter,
          workStateCounts,
          dueDateFilter,
          onDueDateFilterChange: setDueDateFilter,
          dateRangeFilter,
          onDateRangeFilterChange: setDateRangeFilter,
          requestTypeFilter,
          onRequestTypeFilterChange: setRequestTypeFilter,
          requestTypeCounts,
          assigneeFilter: assigneeAdvancedFilter,
          onAssigneeFilterChange: setAssigneeAdvancedFilter,
          assigneeOptions: assigneeFilterOptions,
          onClearAll: handleClearAllAdvancedFilters,
        }),
        getSortOptionsPanelProps: (): SortOptionsPanelProps => ({
          isOpen: isSortOptionsOpen,
          onClose: handleSortOptionsClose,
          dateField,
          onDateFieldChange: handleDateFieldChange,
        }),
      }),
      [
        scrollToSelected,
        scrollToTop,
        isAdvancedFiltersOpen,
        handleAdvancedFiltersClose,
        workStateFilter,
        workStateCounts,
        dueDateFilter,
        dateRangeFilter,
        requestTypeFilter,
        requestTypeCounts,
        assigneeAdvancedFilter,
        assigneeFilterOptions,
        handleClearAllAdvancedFilters,
        isSortOptionsOpen,
        handleSortOptionsClose,
        dateField,
        handleDateFieldChange,
      ]
    );

    const handleRefresh = useCallback(async () => {
      setIsRefreshing(true);
      try {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['queueRequests', activeView], exact: false }),
          queryClient.refetchQueries({ queryKey: ['queueRequestsCount'], exact: false }),
        ]);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        setTimeout(scrollToSelected, 200);
      }
    }, [queryClient, scrollToSelected, activeView]);

    // Use native event listeners for touch events with { passive: false }
    // This allows us to call preventDefault() to stop scrolling during pull
    useEffect(() => {
      const listEl = listRef.current;
      if (!listEl) return;

      const isAtTop = () => listEl.scrollTop <= 5;

      const onTouchStart = (e: TouchEvent) => {
        if (isAtTop() && !isRefreshingRef.current) {
          pullStartY.current = e.touches[0].clientY;
        }
      };

      const onTouchMove = (e: TouchEvent) => {
        if (pullStartY.current === null || isRefreshingRef.current) return;

        const currentY = e.touches[0].clientY;
        const distance = currentY - pullStartY.current;

        if (distance > 0 && isAtTop()) {
          // Prevent default scroll behavior when pulling
          e.preventDefault();
          // Apply resistance for a rubber-band effect
          const resistedDistance = Math.min(distance * 0.5, PULL_THRESHOLD * 1.5);
          setPullDistance(resistedDistance);
          pullDistanceRef.current = resistedDistance;
        } else if (distance <= 0) {
          // User is scrolling up, cancel pull
          pullStartY.current = null;
          setPullDistance(0);
          pullDistanceRef.current = 0;
        }
      };

      const onTouchEnd = () => {
        if (pullStartY.current === null) return;

        if (pullDistanceRef.current >= PULL_THRESHOLD && !isRefreshingRef.current) {
          handleRefresh();
        } else {
          setPullDistance(0);
        }
        pullStartY.current = null;
      };

      listEl.addEventListener('touchstart', onTouchStart, { passive: true });
      listEl.addEventListener('touchmove', onTouchMove, { passive: false });
      listEl.addEventListener('touchend', onTouchEnd, { passive: true });

      return () => {
        listEl.removeEventListener('touchstart', onTouchStart);
        listEl.removeEventListener('touchmove', onTouchMove);
        listEl.removeEventListener('touchend', onTouchEnd);
      };
    }, [handleRefresh]);

    // Get users for assignment dropdown
    const assigneeUsers = useMemo(() => {
      if (!locationUsers) return [];
      return (locationUsers as any[]).map(u => ({
        userId: u.userId,
        name: u.name || u.username || u.email,
        email: u.email,
        type: deriveUserType({ ...u, name: u.name || u.username || u.email }),
      }));
    }, [locationUsers]);

    // Current user for "Assign to me"
    const { user } = useAuthContext();
    const currentUserForAssign = useMemo(() => {
      if (!user) return undefined;
      return {
        userId: meId,
        name: user.displayName || user.email || 'Me',
      };
    }, [user, meId]);

    // Legacy click handler (for backwards compatibility)
    const handleRequestClick = useCallback(
      (request: AskRequest) => {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
        onRequestSelect?.(request);
      },
      [dispatch, onRequestSelect]
    );

    // Get assignee info
    const getAssignee = (
      request: AskRequest
    ): { name?: string; type: AssignedToType; isExplicit: boolean } | undefined => {
      if (request.assignee && locationUsers) {
        const assignedUser = locationUsers.find((u: any) => u.userId === request.assignee) as any;
        if (assignedUser) {
          const name = assignedUser.name || assignedUser.username || assignedUser.email;
          const type = deriveUserType({ ...assignedUser, name });
          return { name, type, isExplicit: true };
        }
      }
      if (!request.comments || request.comments.length === 0) return undefined;
      const lastComment = request.comments[request.comments.length - 1];
      // @ts-ignore
      const name = lastComment?.user?.name || lastComment?.userLocation?.username;
      if (request.type === 'Email') {
        return { name, type: 'ext-email', isExplicit: false };
      }
      const userForType = {
        name: name || '',
        role: lastComment?.userLocation?.role,
        isHoneInternalUser: lastComment?.user?.isInternalUser || lastComment?.userLocation?.isHoneInternalUser,
        userId: lastComment?.userLocation?.userId || '',
      };
      const type = deriveUserType(userForType);
      return { name, type, isExplicit: false };
    };

    // Use the callback versions for hasMention and hasAttachment
    // (defined earlier for quick filter counts)
    const hasMention = checkHasMention;
    const hasAttachment = checkHasAttachment;

    // Check if request is internal
    const isInternal = (request: AskRequest): boolean => {
      if (!request.comments || request.comments.length === 0) return false;
      return request.comments.every((comment: any) => {
        const visibility = comment?.visibility?.toLowerCase();
        return visibility === 'internal';
      });
    };

    // Format date for card display
    const formatCardDate = (date: Date): string => {
      return format(date, 'MMM d, h:mm a');
    };

    return (
      <div className={prefix()}>
        {/* Filter Chips */}
        <div ref={filterChipsRef}>
          <MobileFilterChips
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            activeFilter={activeView}
            onFilterChange={handleFilterChange}
            counts={viewCounts}
            isAccountant={isAccountant}
            isDragDropEnabled={isDragDropActive}
            dragOverFilter={dragOverFilter}
            // Quick filter props
            mentionFilterActive={mentionFilterActive}
            attachmentFilterActive={attachmentFilterActive}
            assignedFilterActive={assignedFilterActive}
            mentionCount={quickFilterCounts.mentionCount}
            attachmentCount={quickFilterCounts.attachmentCount}
            assignedCount={quickFilterCounts.assignedCount}
            onMentionFilterToggle={handleMentionFilterToggle}
            onAttachmentFilterToggle={handleAttachmentFilterToggle}
            onAssignedFilterToggle={handleAssignedFilterToggle}
            // Advanced filter props
            hasActiveAdvancedFilter={hasActiveAdvancedFilter}
            onAdvancedFilterClick={handleAdvancedFilterClick}
            // Sort props
            sortAscending={sortAscending}
            onSortToggle={handleSortToggle}
            dateField={dateField}
            onSortOptionsClick={handleSortOptionsClick}
            // Groups props
            groupCount={groups.length}
            isGroupViewActive={activeView === 'Groups'}
            onGroupsChipClick={handleGroupsChipClick}
            groupFilterActive={groupFilterActive}
            onGroupFilterToggle={handleGroupFilterToggle}
          />
        </div>

        {/* Multi-Select Bar - only shows with 2+ items selected */}
        <MultiSelectBar
          selectedCount={selectedIds.length}
          totalCount={filteredRequests.length}
          isVisible={isSelectionMode && selectedIds.length >= 2}
          activeView={activeView}
          mobile
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onAssign={handleBulkAssign}
          onSetDueDate={handleBulkSetDueDate}
          onResolve={handleBulkResolve}
          onReopen={handleBulkReopen}
          onToggleBlocker={handleBulkToggleBlocker}
          assigneeUsers={assigneeUsers}
          currentUser={currentUserForAssign}
          groups={groups}
          currentGroupId={state.activeGroupId}
          onCreateGroup={handleCreateGroup}
          onAddToGroup={handleMultiSelectAddToGroup}
          onRemoveFromGroup={handleRemoveFromGroup}
        />

        {/* Request List */}
        <div ref={listRef} className={prefix('__list')}>
          {/* Pull-to-refresh indicator */}
          <div
            className={classNames(prefix('__pull-indicator'), {
              [prefix('__pull-indicator--active')]: pullDistance > 0 || isRefreshing,
              [prefix('__pull-indicator--ready')]: pullDistance >= PULL_THRESHOLD,
              [prefix('__pull-indicator--refreshing')]: isRefreshing,
            })}
            style={{ height: isRefreshing ? PULL_THRESHOLD : pullDistance }}
          >
            <div className={prefix('__pull-spinner')} />
            <span className={prefix('__pull-text')}>
              {isRefreshing
                ? 'Refreshing...'
                : pullDistance >= PULL_THRESHOLD
                  ? 'Release to refresh'
                  : 'Pull to refresh'}
            </span>
          </div>

          {isQueryLoading ? (
            <MobileSkeletonLoading count={viewCounts[activeView || 'Me'] || 5} />
          ) : dateGroups.length === 0 && filteredViewBlockers.length === 0 ? (
            <EmptyState
              icon={<IconTaskNew />}
              title="No To-Do's"
              description={searchQuery ? 'Try adjusting your search' : 'No items match this filter'}
            />
          ) : (
            <>
              {/* Blockers Section (at top of Me/Team/Client views) */}
              {filteredViewBlockers.length > 0 && (
                <div className={prefix('__group', 'blockers')}>
                  <button
                    type="button"
                    className={classNames(prefix('__group-header'), 'blockers', {
                      collapsed: collapsedGroups.has('blockers'),
                    })}
                    onClick={() => handleGroupHeaderTap('blockers')}
                  >
                    <span className={prefix('__group-label')}>Blockers</span>
                    <span className={prefix('__group-count')}>{filteredViewBlockers.length}</span>
                    <span className={prefix('__group-chevron')}>
                      {collapsedGroups.has('blockers') ? <IconChevron /> : <IconChevronDown />}
                    </span>
                  </button>

                  {!collapsedGroups.has('blockers') && (
                    <div className={prefix('__group-items')}>
                      {filteredViewBlockers.map((request: AskRequest) => {
                        if (request.type === 'Group') {
                          return (
                            <MobileCollapsibleGroupCard
                              key={request.id}
                              group={request}
                              allRequests={queryRequests}
                              activeView={activeView}
                              userId={meId}
                              selectedIds={selectedIds}
                              isSelectionMode={isSelectionMode}
                              onGroupTap={handleRequestTap}
                              onChildTap={handleRequestTap}
                              onChildLongPress={handleSwipeRightSelect}
                              onChildSwipeAction={handleSwipeAction}
                              getAssignee={getAssignee}
                              getWorkState={getWorkState}
                              checkHasMention={hasMention}
                              checkHasAttachment={hasAttachment}
                              isInternal={isInternal}
                              formatCardDate={formatCardDate}
                              searchQuery={searchQuery}
                              requestCardUsers={requestCardUsers}
                              isAccountant={isAccountant}
                              totalGroupChildCount={groups.find(g => g.id === request.id)?.requestCount}
                              getShortId={getShortId}
                              filterChildren={filterChildRequests}
                            />
                          );
                        }

                        const lastActivityDate = getLastActivityDate(request);
                        const memoText = request.comments?.[request.comments.length - 1]?.comment;
                        const isSelected = selectedIds.includes(request.id);
                        const isFocused = selectedItem?.id === request.id && isSelected;

                        return (
                          <div
                            key={request.id}
                            data-request-id={request.id}
                            className={classNames(prefix('__card-wrapper'), {
                              selected: isSelected,
                            })}
                          >
                            <SwipeableRequestCard
                              id={request.id}
                              request={request}
                              canManageQueue={isAccountant}
                              title={request.title || request.identifier || `Request #${getShortId(request.id)}`}
                              date={formatCardDate(lastActivityDate)}
                              assignee={getAssignee(request)}
                              memo={memoText}
                              users={requestCardUsers}
                              amount={
                                request.flaggedAmount
                                  ? parseFloat(String(request.flaggedAmount))
                                  : request.transaction?.amount
                              }
                              workState={getWorkState(request)}
                              dueDate={request.dueDate ? parseLocalDate(request.dueDate) : undefined}
                              hasMention={hasMention(request)}
                              hasAttachment={hasAttachment(request)}
                              isInternal={isInternal(request)}
                              isFocused={isFocused}
                              isSelected={isSelected}
                              searchQuery={searchQuery}
                              activeView={activeView}
                              selectedIds={selectedIds}
                              isSelectionMode={isSelectionMode}
                              onTap={() => handleRequestTap(request)}
                              onLongPress={() => handleSwipeRightSelect(request)}
                              onSwipeAction={action => handleSwipeAction(action, request)}
                              onDragStart={handleDragStart}
                              onDragMove={handleDragMove}
                              onDragEnd={handleDragEnd}
                              onDragCancel={handleDragCancel}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Date-grouped requests */}
              {dateGroups.map(group => (
                <div key={group.key} className={prefix('__group')}>
                  {/* Group Header - double-tap to toggle all */}
                  <button
                    type="button"
                    className={classNames(prefix('__group-header'), {
                      collapsed: collapsedGroups.has(group.key),
                    })}
                    onClick={() => handleGroupHeaderTap(group.key)}
                  >
                    <span className={prefix('__group-label')}>{group.label}</span>
                    <span className={prefix('__group-count')}>{group.requests.length}</span>
                    <span className={prefix('__group-chevron')}>
                      {collapsedGroups.has(group.key) ? <IconChevron /> : <IconChevronDown />}
                    </span>
                  </button>

                  {/* Group Items */}
                  {!collapsedGroups.has(group.key) && (
                    <div className={prefix('__group-items')}>
                      {group.requests.map((request: AskRequest) => {
                        if (request.type === 'Group') {
                          return (
                            <MobileCollapsibleGroupCard
                              key={request.id}
                              group={request}
                              allRequests={queryRequests}
                              activeView={activeView}
                              userId={meId}
                              selectedIds={selectedIds}
                              isSelectionMode={isSelectionMode}
                              onGroupTap={handleRequestTap}
                              onChildTap={handleRequestTap}
                              onChildLongPress={handleSwipeRightSelect}
                              onChildSwipeAction={handleSwipeAction}
                              getAssignee={getAssignee}
                              getWorkState={getWorkState}
                              checkHasMention={hasMention}
                              checkHasAttachment={hasAttachment}
                              isInternal={isInternal}
                              formatCardDate={formatCardDate}
                              searchQuery={searchQuery}
                              requestCardUsers={requestCardUsers}
                              isAccountant={isAccountant}
                              totalGroupChildCount={groups.find(g => g.id === request.id)?.requestCount}
                              getShortId={getShortId}
                              filterChildren={filterChildRequests}
                            />
                          );
                        }

                        const lastActivityDate = getLastActivityDate(request);
                        const memoText = request.comments?.[request.comments.length - 1]?.comment;
                        const isSelected = selectedIds.includes(request.id);
                        const isFocused = selectedItem?.id === request.id && isSelected;

                        return (
                          <div
                            key={request.id}
                            data-request-id={request.id}
                            className={classNames(prefix('__card-wrapper'), {
                              selected: isSelected,
                            })}
                          >
                            <SwipeableRequestCard
                              id={request.id}
                              request={request}
                              canManageQueue={isAccountant}
                              title={request.title || request.identifier || `Request #${getShortId(request.id)}`}
                              date={formatCardDate(lastActivityDate)}
                              assignee={getAssignee(request)}
                              memo={memoText}
                              users={requestCardUsers}
                              amount={
                                request.flaggedAmount
                                  ? parseFloat(String(request.flaggedAmount))
                                  : request.transaction?.amount
                              }
                              workState={getWorkState(request)}
                              dueDate={request.dueDate ? parseLocalDate(request.dueDate) : undefined}
                              hasMention={hasMention(request)}
                              hasAttachment={hasAttachment(request)}
                              isInternal={isInternal(request)}
                              isFocused={isFocused}
                              isSelected={isSelected}
                              searchQuery={searchQuery}
                              activeView={activeView}
                              selectedIds={selectedIds}
                              isSelectionMode={isSelectionMode}
                              onTap={() => handleRequestTap(request)}
                              onLongPress={() => handleSwipeRightSelect(request)}
                              onSwipeAction={action => handleSwipeAction(action, request)}
                              onDragStart={handleDragStart}
                              onDragMove={handleDragMove}
                              onDragEnd={handleDragEnd}
                              onDragCancel={handleDragCancel}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {/* Drag indicator - follows finger during drag-to-filter */}
        {isDragDropActive && dragPosition && (
          <div
            className={prefix('__drag-indicator')}
            style={{
              left: dragPosition.x,
              top: dragPosition.y,
            }}
          >
            <span className={prefix('__drag-indicator-count')}>{selectedIds.length}</span>
          </div>
        )}

        {/* Panels rendered via portal to mobile-panels-root for consistent overlay stacking */}
        {typeof document !== 'undefined' &&
          document.getElementById('mobile-panels-root') &&
          createPortal(
            <>
              <MobileAdvancedFiltersPanel
                isOpen={isAdvancedFiltersOpen}
                onClose={handleAdvancedFiltersClose}
                workStateFilter={workStateFilter}
                onWorkStateFilterChange={setWorkStateFilter}
                workStateCounts={workStateCounts}
                dueDateFilter={dueDateFilter}
                onDueDateFilterChange={setDueDateFilter}
                dateRangeFilter={dateRangeFilter}
                onDateRangeFilterChange={setDateRangeFilter}
                requestTypeFilter={requestTypeFilter}
                onRequestTypeFilterChange={setRequestTypeFilter}
                requestTypeCounts={requestTypeCounts}
                assigneeFilter={assigneeAdvancedFilter}
                onAssigneeFilterChange={setAssigneeAdvancedFilter}
                assigneeOptions={assigneeFilterOptions}
                onClearAll={handleClearAllAdvancedFilters}
              />
              <MobileSortOptionsPanel
                isOpen={isSortOptionsOpen}
                onClose={handleSortOptionsClose}
                dateField={dateField}
                onDateFieldChange={handleDateFieldChange}
              />
              <MobileGroupPickerPanel
                isOpen={isGroupPickerOpen}
                onClose={() => {
                  setIsGroupPickerOpen(false);
                  setPendingDropIds([]);
                }}
                groups={groups}
                activeGroupId={state.activeGroupId ?? null}
                onGroupSelect={handleGroupPickerSelect}
                mode={groupPickerMode}
              />
            </>,
            document.getElementById('mobile-panels-root')!
          )}

        {/* Resolve Dialog */}
        <ResolveDialog
          isOpen={isResolveOpen}
          isPending={patchMutation.isPending}
          onClose={() => {
            setIsResolveOpen(false);
            setResolveTarget(null);
          }}
          onSubmit={handleResolveSubmit}
        />

        <ReopenDialog
          isOpen={isReopenOpen}
          isPending={patchMutation.isPending}
          onClose={() => {
            setIsReopenOpen(false);
            setReopenTarget(null);
          }}
          onSubmit={handleReopenSubmit}
        />

        <ResolveDialog
          isOpen={isBulkResolveOpen}
          isPending={patchMutation.isPending}
          onClose={() => setIsBulkResolveOpen(false)}
          onSubmit={handleBulkResolveSubmit}
          title="Resolve Requests"
          commentLabel="Closing comment (optional)"
          commentPlaceholder="Add a closing comment to explain the resolution."
          submitLabel="Resolve All"
        />

        <ReopenDialog
          isOpen={isBulkReopenOpen}
          isPending={patchMutation.isPending}
          onClose={() => {
            setIsBulkReopenOpen(false);
            setBulkReopenInfo(null);
          }}
          onSubmit={handleBulkReopenSubmit}
          title="Reopen Requests"
          commentLabel="Reopen comment (optional)"
          commentPlaceholder="Add a note explaining why these requests are being reopened."
          submitLabel="Reopen All"
          infoMessage={bulkReopenInfo ?? undefined}
        />
      </div>
    );
  }
);

// Display name for debugging
MobileRequestList.displayName = 'MobileRequestList';

export default MobileRequestList;
